import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../styles/pages/Products.scss';
import { MdFilterList, MdClose } from "react-icons/md";
import axios from 'axios';
import { SERVER } from '../hooks/config';
import prod1 from '../assets/products/prod1.png';
import ProductCard from '../components/ProductCard';
import { MdChevronRight, MdChevronLeft } from "react-icons/md";
import LoadingComponent from '../components/LoadingComponent';

const Products = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [categories, setCategories] = useState([]);
    const [showFilter, setShowFilter] = useState(false);
    const [filterOptions, setFilterOptions] = useState({
        category: '',
        minPrice: '',
        maxPrice: '',
        promotion: false,
        new_product: false,
        materials: [],
        dimensions: []
    });
    const [appliedFilters, setAppliedFilters] = useState({
        category: '',
        minPrice: '',
        maxPrice: '',
        promotion: false,
        new_product: false,
        materials: [],
        dimensions: []
    });
    const [availableMaterials, setAvailableMaterials] = useState([]);
    const [availableDimensions, setAvailableDimensions] = useState([]);
    const [urlFilter, setUrlFilter] = useState('');
    const [hasInitialized, setHasInitialized] = useState(false);
    const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
    const [priceValues, setPriceValues] = useState({ min: 0, max: 1000 });
    const productsPerPage = 12;

    // Handle URL parameters on component mount
    useEffect(() => {
        const categoryIdFromUrl = searchParams.get('category_id');
        const filterFromUrl = searchParams.get('filter');
        
        console.log('URL params:', { categoryIdFromUrl, filterFromUrl });
        
        // Set category filter if present
        if (categoryIdFromUrl) {
            setSelectedCategory(categoryIdFromUrl);
            setFilterOptions(prev => ({
                ...prev,
                category: categoryIdFromUrl
            }));
            setAppliedFilters(prev => ({
                ...prev,
                category: categoryIdFromUrl
            }));
        }
        
        // Set URL filter if present
        if (filterFromUrl) {
            setUrlFilter(filterFromUrl);
            
            if (filterFromUrl === 'new') {
                console.log('Setting new product filter');
                setFilterOptions(prev => ({
                    ...prev,
                    new_product: true,
                    promotion: false
                }));
                setAppliedFilters(prev => ({
                    ...prev,
                    new_product: true,
                    promotion: false
                }));
            } else if (filterFromUrl === 'promo') {
                console.log('Setting promotion filter');
                setFilterOptions(prev => ({
                    ...prev,
                    promotion: true,
                    new_product: false
                }));
                setAppliedFilters(prev => ({
                    ...prev,
                    promotion: true,
                    new_product: false
                }));
            }
            
            // Mark as initialized and don't clear URL immediately
            setHasInitialized(true);
        } else {
            setHasInitialized(true);
        }
    }, [searchParams]);

    // Clear URL parameter after initialization
    useEffect(() => {
        if (hasInitialized && searchParams.get('filter')) {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('filter');
            setSearchParams(newSearchParams, { replace: true });
        }
    }, [hasInitialized, searchParams, setSearchParams]);

    useEffect(() => {
        fetchCategories();
        fetchFilterOptions();
    }, []);

    useEffect(() => {
        if (hasInitialized) {
            fetchProducts();
        }
    }, [currentPage, searchTerm, appliedFilters, urlFilter, hasInitialized]);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            let params = {
                page: currentPage,
                limit: productsPerPage,
                search: searchTerm || undefined,
                category_id: appliedFilters.category || selectedCategory || undefined,
                min_price: appliedFilters.minPrice || undefined,
                max_price: appliedFilters.maxPrice || undefined,
                promotion: appliedFilters.promotion || undefined,
                new_product: appliedFilters.new_product || undefined,
                materials: appliedFilters.materials.length > 0 ? appliedFilters.materials.join(',') : undefined,
                dimensions: appliedFilters.dimensions.length > 0 ? appliedFilters.dimensions.join(',') : undefined
            };

            // Override with URL filter if present (highest priority)
            if (urlFilter === 'new') {
                console.log('Applying new product filter to API call');
                params = {
                    ...params,
                    new_product: true,
                    promotion: undefined
                };
            } else if (urlFilter === 'promo') {
                console.log('Applying promotion filter to API call');
                params = {
                    ...params,
                    promotion: true,
                    new_product: undefined
                };
            }

            console.log('Final API params:', params);

            const response = await axios.get(`${SERVER}/products`, { params });
            setProducts(response.data.products);
            setTotalPages(response.data.pagination.total_pages);
            setTotalProducts(response.data.pagination.total_products);
            
            // Update price range based on products
            if (response.data.products.length > 0) {
                const prices = response.data.products.map(p => p.price);
                const minPrice = Math.min(...prices);
                const maxPrice = Math.max(...prices);
                setPriceRange({ min: minPrice, max: maxPrice });
                setPriceValues({ min: minPrice, max: maxPrice });
            }
            
            console.log('Products fetched:', response.data.products.length);
        } catch (error) {
            console.error('Error fetching products:', error);
            setProducts([]);
            setTotalPages(1);
            setTotalProducts(0);
        } finally {
            setLoading(false);
        }
    };

    const clearUrlFilter = () => {
        setUrlFilter('');
        setFilterOptions(prev => ({
            ...prev,
            promotion: false,
            new_product: false
        }));
        setAppliedFilters(prev => ({
            ...prev,
            promotion: false,
            new_product: false
        }));
        setCurrentPage(1);
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${SERVER}/categories`);
            setCategories(response.data);
        } catch (error) {
            console.error('Error fetching categories:', error);
            setCategories([]);
        }
    };

    const fetchFilterOptions = async () => {
        try {
            // Fetch all products to extract unique materials and dimensions
            const response = await axios.get(`${SERVER}/products?limit=1000`);
            const allProducts = response.data.products;
            
            // Extract unique materials
            const materials = new Set();
            const dimensions = new Set();
            
            allProducts.forEach(product => {
                if (product.materiel) {
                    materials.add(product.materiel);
                }
                if (product.dimension) {
                    dimensions.add(product.dimension);
                }
            });
            
            setAvailableMaterials(Array.from(materials).sort());
            setAvailableDimensions(Array.from(dimensions).sort());
        } catch (error) {
            console.error('Error fetching filter options:', error);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleCategoryFilter = (e) => {
        const categoryId = e.target.value;
        setSelectedCategory(categoryId);
        setFilterOptions(prev => ({
            ...prev,
            category: categoryId
        }));
        setAppliedFilters(prev => ({
            ...prev,
            category: categoryId
        }));
        setCurrentPage(1);
    };

    const handleFilterChange = (key, value) => {
        setFilterOptions(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleMaterialToggle = (material) => {
        setFilterOptions(prev => ({
            ...prev,
            materials: prev.materials.includes(material)
                ? prev.materials.filter(m => m !== material)
                : [...prev.materials, material]
        }));
    };

    const handleDimensionToggle = (dimension) => {
        setFilterOptions(prev => ({
            ...prev,
            dimensions: prev.dimensions.includes(dimension)
                ? prev.dimensions.filter(d => d !== dimension)
                : [...prev.dimensions, dimension]
        }));
    };

    const handlePriceChange = (e, type) => {
        const value = parseInt(e.target.value);
        if (type === 'min') {
            setPriceValues(prev => ({ ...prev, min: value }));
            setFilterOptions(prev => ({ ...prev, minPrice: value }));
        } else {
            setPriceValues(prev => ({ ...prev, max: value }));
            setFilterOptions(prev => ({ ...prev, maxPrice: value }));
        }
    };

    const clearFilters = () => {
        setFilterOptions({
            category: '',
            minPrice: '',
            maxPrice: '',
            promotion: false,
            new_product: false,
            materials: [],
            dimensions: []
        });
        setAppliedFilters({
            category: '',
            minPrice: '',
            maxPrice: '',
            promotion: false,
            new_product: false,
            materials: [],
            dimensions: []
        });
        setSelectedCategory('');
        setSearchTerm('');
        setPriceValues({ min: priceRange.min, max: priceRange.max });
        setCurrentPage(1);
    };

    const applyFilters = () => {
        setAppliedFilters(filterOptions);
        setCurrentPage(1);
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const goToPrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };

    if (loading) {
        return <LoadingComponent />;
    }

    return (
        <div className='products'>
            <div className='products-general-container'>
                <h1> 
                    {urlFilter === 'new' ? 'Nouveautés' : 
                     urlFilter === 'promo' ? 'Promotion' : 
                     'Tous les produits'} 
                </h1>
                <div className='utils'>
                    <div className='filters'>
                        <button 
                            className='filter-btn'
                            onClick={() => setShowFilter(!showFilter)}
                        >
                            <MdFilterList size={21} /> Filtres
                        </button>
                    </div>
                    <span>{totalProducts} Produits</span>
                </div>
                
                <div className='products-content'>
                    {/* Filter Sidebar */}
                    {showFilter && (
                        <div className="filter-sidebar">
                            <div className="filter-sidebar-content">
                                <div className="filter-section">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={filterOptions.promotion}
                                            onChange={(e) => handleFilterChange('promotion', e.target.checked)}
                                        />
                                        <span>Promotions</span>
                                    </label>
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={filterOptions.new_product}
                                            onChange={(e) => handleFilterChange('new_product', e.target.checked)}
                                        />
                                        <span>Nouveautés</span>
                                    </label>
                                </div>
                                <div className="filter-section">
                                    <h4>Catégories</h4>
                                    <div className="category-checkboxes">
                                        {categories.map(category => (
                                            <label key={category.id} className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={filterOptions.category === category.id}
                                                    onChange={() => handleFilterChange('category', filterOptions.category === category.id ? '' : category.id)}
                                                />
                                                <span>{category.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="filter-section">
                                    <h4>Par matière</h4>
                                    <div className="filter-checkboxes">
                                        {availableMaterials.map(material => (
                                            <label key={material} className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={filterOptions.materials.includes(material)}
                                                    onChange={() => handleMaterialToggle(material)}
                                                />
                                                <span>{material}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="filter-section">
                                    <h4>Par dimensions</h4>
                                    <div className="filter-checkboxes">
                                        {availableDimensions.map(dimension => (
                                            <label key={dimension} className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={filterOptions.dimensions.includes(dimension)}
                                                    onChange={() => handleDimensionToggle(dimension)}
                                                />
                                                <span>{dimension}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="filter-section">
                                    <h4>Par prix</h4>
                                    <div className="price-slider-container">
                                        <div className="price-slider">
                                            <div className="slider-track"></div>
                                            <input
                                                type="range"
                                                min={priceRange.min}
                                                max={priceRange.max}
                                                value={priceValues.min}
                                                onChange={(e) => handlePriceChange(e, 'min')}
                                                className="slider slider-min"
                                            />
                                            <input
                                                type="range"
                                                min={priceRange.min}
                                                max={priceRange.max}
                                                value={priceValues.max}
                                                onChange={(e) => handlePriceChange(e, 'max')}
                                                className="slider slider-max"
                                            />
                                        </div>
                                        <div className="price-values">
                                            <span>{priceValues.min} DT</span>
                                            <span>{priceValues.max} DT</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-actions">
                                    <button className="clear-filters" onClick={clearFilters}>
                                        Effacer tous les filtres
                                    </button>
                                    <button className="apply-filters" onClick={applyFilters}>
                                        Appliquer les filtres
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Products Container */}
                    <div className={`products-container ${showFilter ? 'with-filter' : ''}`}>
                        {products.length > 0 ? (
                            <div className='products-yes'>
                                {products.map((product) => (
                                    <ProductCard
                                        product={product}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className='no-products'>Aucun produit trouvé.</div>
                        )}
                    </div>
                </div>
    
                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="pagination">
                        <button onClick={goToPrevPage} disabled={currentPage === 1}>
                            <MdChevronLeft />
                        </button>
                    
                        {Array.from({ length: totalPages }, (_, i) => (
                            <button
                                key={i}
                                onClick={() => setCurrentPage(i + 1)}
                                className={currentPage === i + 1 ? 'active' : ''}
                            >
                                {i + 1}
                            </button>
                        ))}
                    
                        <button onClick={goToNextPage} disabled={currentPage === totalPages}>
                            <MdChevronRight />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Products;